import React, { PropsWithChildren } from 'react';
import styled from '@emotion/styled';
import { screens, semantic_colors, semantic_spacing } from '../../foundation';
import { useProviderConfig } from '../../components/provider';
import { buildCSSWithLength, CSSValueWithLength } from '../../styles';

export interface SectionProps {
  /**
   * 반응형 디자인 사용 여부, desktop: 데스크탑 규격만 사용, mobile: 모바일 규격만 사용, auto: 데스크탑과 모바일 규격을 자동으로 사용
   * @default auto
   */
  responsive?: 'auto' | 'desktop' | 'mobile';
  /**
   * 섹션 내부 콘텐츠 패딩 값 커스텀(0 or default)
   */
  padding?: 0;
  /**
   * 섹션 외부 마진 제거 여부
   * @default false
   */
  noMargin?: boolean;
}

export const Section = ({ responsive, padding, noMargin, children }: PropsWithChildren<SectionProps>) => {
  const { components: { section: contextSection } = { section: { responsive: 'auto' } } } = useProviderConfig();
  const mergedResponsive = responsive ?? contextSection?.responsive;

  if (mergedResponsive === 'desktop') {
    return (
      <DesktopContainer className='pds-section' noMargin={noMargin}>
        <DesktopContent padding={padding}>{children}</DesktopContent>
      </DesktopContainer>
    );
  }

  if (mergedResponsive === 'mobile') {
    return (
      <MobileContainer className='pds-section' noMargin={noMargin}>
        <MobileContent padding={padding}>{children}</MobileContent>
      </MobileContainer>
    );
  }

  return (
    <AutoContainer className='pds-section' noMargin={noMargin}>
      <AutoContent padding={padding}>{children}</AutoContent>
    </AutoContainer>
  );
};

const DesktopContainer = styled.section<{ noMargin?: boolean }>`
  width: 100%;
  padding: ${({ noMargin }) => (noMargin ? 0 : semantic_spacing.section.margin.desktop)};
  & + & {
    padding-top: 0;
  }
`;

const DesktopContent = styled.div<{ padding?: CSSValueWithLength }>`
  background-color: ${semantic_colors.background.surface};
  border-radius: 16px;
  ${({ padding }) =>
    padding !== undefined
      ? buildCSSWithLength('padding', padding)
      : `padding: ${semantic_spacing.section.padding_vertical.desktop} ${semantic_spacing.section.padding_horizontal.desktop}`};
`;

const MobileContainer = styled.section<{ noMargin?: boolean }>`
  width: 100%;
  & + & {
    margin-top: ${({ noMargin }) => (noMargin ? 0 : '8px')};
  }
`;

const MobileContent = styled.div<{ padding?: CSSValueWithLength }>`
  background-color: ${semantic_colors.background.surface};
  ${({ padding }) =>
    padding !== undefined
      ? buildCSSWithLength('padding', padding)
      : `padding: ${semantic_spacing.section.padding_vertical.mobile} ${semantic_spacing.section.padding_horizontal.mobile}`};
`;

const AutoContainer = styled.section<{ noMargin?: boolean }>`
  width: 100%;
  & + & {
    padding-top: ${({ noMargin }) => (noMargin ? 0 : '8px')};
  }
  @media (min-width: ${screens.desktop}) {
    padding: ${({ noMargin }) => (noMargin ? 0 : semantic_spacing.section.margin.desktop)};
    & + & {
      padding-top: 0;
      margin-top: 0;
    }
  }
`;

const AutoContent = styled.div<{ padding?: CSSValueWithLength }>`
  background-color: ${semantic_colors.background.surface};
  ${({ padding }) =>
    padding !== undefined
      ? buildCSSWithLength('padding', padding)
      : `padding: ${semantic_spacing.section.padding_vertical.mobile} ${semantic_spacing.section.padding_horizontal.mobile}`};
  @media (min-width: ${screens.desktop}) {
    border-radius: 16px;
    ${({ padding }) =>
      padding !== undefined
        ? buildCSSWithLength('padding', padding)
        : `padding:${semantic_spacing.section.padding_vertical.desktop} ${semantic_spacing.section.padding_horizontal.desktop}`};
  }
`;
